import { TICK_MS } from "../movement/step.ts";
import type { ProjectileLaunch } from "../protocol/projectiles.ts";
import { projectileVariation } from "./projectile-point.ts";
import { projectileLandsAt } from "./projectile-timing.ts";

/**
 * The shots a page holds (ADR-095 decision 21): flights keyed by the shot's id and the bursts they end in, their state
 * in typed arrays made once, in two pools of slots that start at 64, double when exhausted up to 256, and beyond that
 * recycle the oldest. A launch whose id is held replaces its copy, so a launch sent again (after a dropped frame, or to
 * a page that joined mid-flight) never draws a second shot; one sent again while its burst plays is left out, since the
 * page landed it early (decision 17). The record is plain data with top-level functions, each allocating nothing, so a
 * drawn frame costs no allocation; the view that draws it (`odyssey-client/src/game/world/projectile-view.ts`) keeps one
 * sprite a slot.
 */

/**
 * How many slots a pool starts with.
 */
export const PROJECTILE_POOL_START = 64;

/**
 * The most slots a pool grows to.
 */
export const PROJECTILE_POOL_MAX = 256;

/**
 * The slot of an id the page holds no shot of.
 */
export const NO_PROJECTILE_SLOT = -1;

const IDS = 0x10000;
const ID_MASK = 0xffff;

/**
 * A pool of slots: how many exist, how many are in use, which are, the order they were claimed in, and the free ones
 * as a stack with the lowest on top. `evicted` says whether the last claim recycled a slot in use.
 */
export interface ProjectilePool {
  capacity: number;
  count: number;
  evicted: boolean;
  freeCount: number;
  clock: number;
  readonly live: Uint8Array;
  readonly born: Float64Array;
  readonly free: Int16Array;
}

/**
 * The page's flights and bursts. A flight's columns are indexed by its slot in `flights`, a burst's (`burst...`) by its
 * slot in `bursts`; `flightOfId` and `burstOfId` give the slot an id holds, or `NO_PROJECTILE_SLOT`. The counters say
 * how many launches started a flight, replaced one held, were left out while their burst played, and how many flights
 * and bursts were recycled while still shown. `landsAtMs` is when the page lands a flight, early by its lead, and
 * `judgedAtMs` when the map server judges it, with no lead.
 */
export interface ProjectileFlights {
  readonly flights: ProjectilePool;
  readonly bursts: ProjectilePool;
  readonly flightOfId: Int16Array;
  readonly burstOfId: Int16Array;
  readonly id: Uint16Array;
  readonly ability: Uint16Array;
  readonly originX: Uint16Array;
  readonly originY: Uint16Array;
  readonly landingX: Uint16Array;
  readonly landingY: Uint16Array;
  readonly flightTicks: Uint8Array;
  readonly age: Uint8Array;
  readonly ringed: Uint8Array;
  readonly arrivalMs: Float64Array;
  readonly landsAtMs: Float64Array;
  readonly judgedAtMs: Float64Array;
  readonly burstMs: Float64Array;
  readonly variation: Uint32Array;
  readonly burstId: Uint16Array;
  readonly burstAbility: Uint16Array;
  readonly burstX: Uint16Array;
  readonly burstY: Uint16Array;
  readonly burstStartMs: Float64Array;
  readonly burstEndMs: Float64Array;
  readonly burstVariation: Uint32Array;
  launched: number;
  replaced: number;
  ignored: number;
  recycled: number;
  burstsRecycled: number;
}

/**
 * Puts the slots from one index up to the capacity on the free stack, the lowest on top.
 *
 * @param pool The pool.
 * @param from The first slot to free.
 */
function freeFrom(pool: ProjectilePool, from: number): void {
  for (let slot = pool.capacity - 1; slot >= from; slot -= 1) {
    pool.free[pool.freeCount] = slot;
    pool.freeCount += 1;
  }
}

/**
 * A pool of `PROJECTILE_POOL_START` free slots.
 *
 * @returns The pool.
 */
export function newProjectilePool(): ProjectilePool {
  const pool: ProjectilePool = { capacity: PROJECTILE_POOL_START, count: 0, evicted: false, freeCount: 0, clock: 0, live: new Uint8Array(PROJECTILE_POOL_MAX), born: new Float64Array(PROJECTILE_POOL_MAX), free: new Int16Array(PROJECTILE_POOL_MAX) };
  freeFrom(pool, 0);
  return pool;
}

/**
 * The slot in use that was claimed longest ago.
 *
 * @param pool A pool with every slot in use.
 * @returns The slot.
 */
function oldestSlot(pool: ProjectilePool): number {
  let oldest = 0;
  for (let slot = 1; slot < pool.capacity; slot += 1) {
    if ((pool.born[slot] as number) < (pool.born[oldest] as number)) {
      oldest = slot;
    }
  }
  return oldest;
}

/**
 * Claims a slot: a free one, lowest first; when none is free the pool doubles, up to `PROJECTILE_POOL_MAX`; and at
 * that size the slot claimed longest ago is recycled, which `pool.evicted` then says, so the caller forgets what it
 * held.
 *
 * @param pool The pool.
 * @returns The slot.
 */
export function claimSlot(pool: ProjectilePool): number {
  pool.evicted = false;
  if (pool.freeCount === 0 && pool.capacity < PROJECTILE_POOL_MAX) {
    const from = pool.capacity;
    pool.capacity = Math.min(PROJECTILE_POOL_MAX, pool.capacity * 2);
    freeFrom(pool, from);
  }
  let slot: number;
  if (pool.freeCount > 0) {
    pool.freeCount -= 1;
    slot = pool.free[pool.freeCount] as number;
    pool.count += 1;
  } else {
    slot = oldestSlot(pool);
    pool.evicted = true;
  }
  pool.live[slot] = 1;
  pool.born[slot] = pool.clock;
  pool.clock += 1;
  return slot;
}

/**
 * Frees a slot. A slot that is free already, or that the pool does not have, is left alone.
 *
 * @param pool The pool.
 * @param slot The slot.
 */
export function releaseSlot(pool: ProjectilePool, slot: number): void {
  if (slot < 0 || slot >= pool.capacity || pool.live[slot] !== 1) {
    return;
  }
  pool.live[slot] = 0;
  pool.free[pool.freeCount] = slot;
  pool.freeCount += 1;
  pool.count -= 1;
}

/**
 * Frees every slot. The pool keeps the capacity it grew to, since a view keeps the sprites it made for it.
 *
 * @param pool The pool.
 */
export function clearPool(pool: ProjectilePool): void {
  pool.live.fill(0);
  pool.count = 0;
  pool.freeCount = 0;
  pool.evicted = false;
  freeFrom(pool, 0);
}

/**
 * A page's shots, none held.
 *
 * @returns The record.
 */
export function newProjectileFlights(): ProjectileFlights {
  const max = PROJECTILE_POOL_MAX;
  return {
    flights: newProjectilePool(),
    bursts: newProjectilePool(),
    flightOfId: new Int16Array(IDS).fill(NO_PROJECTILE_SLOT),
    burstOfId: new Int16Array(IDS).fill(NO_PROJECTILE_SLOT),
    id: new Uint16Array(max),
    ability: new Uint16Array(max),
    originX: new Uint16Array(max),
    originY: new Uint16Array(max),
    landingX: new Uint16Array(max),
    landingY: new Uint16Array(max),
    flightTicks: new Uint8Array(max),
    age: new Uint8Array(max),
    ringed: new Uint8Array(max),
    arrivalMs: new Float64Array(max),
    landsAtMs: new Float64Array(max),
    judgedAtMs: new Float64Array(max),
    burstMs: new Float64Array(max),
    variation: new Uint32Array(max),
    burstId: new Uint16Array(max),
    burstAbility: new Uint16Array(max),
    burstX: new Uint16Array(max),
    burstY: new Uint16Array(max),
    burstStartMs: new Float64Array(max),
    burstEndMs: new Float64Array(max),
    burstVariation: new Uint32Array(max),
    launched: 0,
    replaced: 0,
    ignored: 0,
    recycled: 0,
    burstsRecycled: 0,
  };
}

/**
 * Takes a launch. A launch whose id is in flight replaces that flight in its slot, unless it is the same shot sent
 * again (the same ability, landing point and flight), which is left as the page has it, so a shot already drawn ahead
 * by its lead is not pulled back along its line; one whose id's burst is playing, or whose landing the map server has
 * not yet judged, is left out; any other claims a slot, recycling the oldest flight once 256 are held. Its landing time is
 * `projectileLandsAt` of its arrival, and its variation `projectileVariation` of its id, landing point and ability.
 *
 * @param store The page's shots.
 * @param launch The launch as decoded; the record is read and not kept.
 * @param arrivalMs When it arrived, on the page's clock.
 * @param rttMs The smoothed round trip in ms, null while the page has measured none.
 * @param ringed Whether a landing ring is drawn under the flight (ADR-095 decision 20).
 * @param burstMs How long the look's burst plays at the landing, 0 for a look with none.
 * @returns The flight's slot, or `NO_PROJECTILE_SLOT` for a launch left out.
 */
export function launchProjectile(store: ProjectileFlights, launch: Readonly<ProjectileLaunch>, arrivalMs: number, rttMs: number | null, ringed: boolean, burstMs: number): number {
  const id = launch.id & ID_MASK;
  if (store.burstOfId[id] !== NO_PROJECTILE_SLOT) {
    store.ignored += 1;
    return NO_PROJECTILE_SLOT;
  }
  let slot = store.flightOfId[id] as number;
  if (slot === NO_PROJECTILE_SLOT) {
    slot = claimSlot(store.flights);
    if (store.flights.evicted) {
      store.flightOfId[store.id[slot] as number] = NO_PROJECTILE_SLOT;
      store.recycled += 1;
    }
    store.flightOfId[id] = slot;
    store.launched += 1;
  } else {
    store.replaced += 1;
    if (store.ability[slot] === launch.ability && store.landingX[slot] === launch.landingX && store.landingY[slot] === launch.landingY && store.flightTicks[slot] === launch.flightTicks) {
      return slot;
    }
  }
  store.id[slot] = id;
  store.ability[slot] = launch.ability;
  store.originX[slot] = launch.originX;
  store.originY[slot] = launch.originY;
  store.landingX[slot] = launch.landingX;
  store.landingY[slot] = launch.landingY;
  store.flightTicks[slot] = launch.flightTicks;
  store.age[slot] = launch.age;
  store.ringed[slot] = ringed ? 1 : 0;
  store.arrivalMs[slot] = arrivalMs;
  store.landsAtMs[slot] = projectileLandsAt(arrivalMs, launch.flightTicks, launch.age, rttMs);
  store.judgedAtMs[slot] = arrivalMs + Math.max(0, launch.flightTicks - launch.age) * TICK_MS;
  store.burstMs[slot] = burstMs > 0 ? burstMs : 0;
  store.variation[slot] = projectileVariation(id, launch.landingX, launch.landingY, launch.ability);
  return slot;
}

/**
 * Ends a flight whose landing time has come and starts its burst at the landing point, from the landing time. The
 * burst holds the shot's id until it has played out and the map server's own landing time has passed, whichever is
 * later, so a launch sent again inside the lead draws nothing even for a look with no burst; one that is past both
 * is not started. The 257th burst recycles the oldest.
 *
 * @param store The page's shots.
 * @param slot The flight's slot.
 * @param nowMs The time drawn.
 */
function landFlight(store: ProjectileFlights, slot: number, nowMs: number): void {
  const id = store.id[slot] as number;
  const landed = store.landsAtMs[slot] as number;
  const ends = Math.max(landed + (store.burstMs[slot] as number), store.judgedAtMs[slot] as number);
  releaseSlot(store.flights, slot);
  store.flightOfId[id] = NO_PROJECTILE_SLOT;
  if (ends <= nowMs) {
    return;
  }
  const burst = claimSlot(store.bursts);
  if (store.bursts.evicted) {
    store.burstOfId[store.burstId[burst] as number] = NO_PROJECTILE_SLOT;
    store.burstsRecycled += 1;
  }
  store.burstOfId[id] = burst;
  store.burstId[burst] = id;
  store.burstAbility[burst] = store.ability[slot] as number;
  store.burstX[burst] = store.landingX[slot] as number;
  store.burstY[burst] = store.landingY[slot] as number;
  store.burstStartMs[burst] = landed;
  store.burstEndMs[burst] = ends;
  store.burstVariation[burst] = store.variation[slot] as number;
}

/**
 * Brings the shots to a time: every burst that has played out is dropped, and every flight whose landing time has
 * come ends and bursts.
 *
 * @param store The page's shots.
 * @param nowMs The time drawn, on the clock the launches arrived on.
 */
export function advanceProjectiles(store: ProjectileFlights, nowMs: number): void {
  const bursts = store.bursts;
  for (let slot = 0; slot < bursts.capacity; slot += 1) {
    if (bursts.live[slot] === 1 && (store.burstEndMs[slot] as number) <= nowMs) {
      releaseSlot(bursts, slot);
      store.burstOfId[store.burstId[slot] as number] = NO_PROJECTILE_SLOT;
    }
  }
  const flights = store.flights;
  for (let slot = 0; slot < flights.capacity; slot += 1) {
    if (flights.live[slot] === 1 && (store.landsAtMs[slot] as number) <= nowMs) {
      landFlight(store, slot, nowMs);
    }
  }
}

/**
 * Drops every flight and burst and forgets every id, as every welcome does.
 *
 * @param store The page's shots.
 */
export function clearProjectiles(store: ProjectileFlights): void {
  clearPool(store.flights);
  clearPool(store.bursts);
  store.flightOfId.fill(NO_PROJECTILE_SLOT);
  store.burstOfId.fill(NO_PROJECTILE_SLOT);
}
